import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "@sg/i18n";
import { useEffect, useState } from "react";
import { useInterval } from "react-use";
import useAuthStore from "~/store/auth";
import { isSuperAdminOrg } from "~/util/organization";
import { useMe, useOrgOnDemand } from "../query";

// Auth0 token expiration time is 1 hour.
// If you want to get fancy you can parse the JWT token and get
// token's actual expiration time.
const TOKEN_REFRESH_RATE = 60 * 60 * 1000;

export function useAuthentication() {
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(false);
  const login = useAuthStore((state) => state.login);
  const setAuthToken = useAuthStore((state) => state.setAuthToken);
  const setAuthError = useAuthStore((state) => state.setAuthError);
  const setSuperAdmin = useAuthStore((state) => state.setSuperAdmin);
  const { refetch } = useMe();
  const { mutateAsync } = useOrgOnDemand();

  const { user, isAuthenticated, getAccessTokenSilently, logout } = useAuth0();

  useEffect(() => {
    // Once we're authenticated we need to do a few additional steps to indentify the current user.
    // 1. Fetch the user data so that we can merge it with the user data from auth0 (namely for orgId).
    // 2. Fetch the org data for the user, so we can determine if they are a super admin.
    const storeLogin = async () => {
      const token = await getAccessTokenSilently();
      setAuthToken(token);
      if (!token) {
        console.error("Access token was not successfully fetched.");
      }
      refetch()
        .then(({ data, error }) => {
          if (data && !error) {
            const userData = { ...user, ...data };
            mutateAsync({ org_id: userData.org_id })
              .then(({ domain_name }) => {
                setSuperAdmin(isSuperAdminOrg(domain_name));
                login(userData);
                setRefresh(true);
              })
              .catch((error) => {
                throw Error(error);
              });
          } else {
            throw Error((error as string) || t("error_authenticate"));
          }
        })
        .catch((error) => {
          setAuthError(error);
        });
    };

    if (isAuthenticated) {
      storeLogin();
    }
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    login,
    logout,
    refetch,
    setAuthToken,
    setAuthError,
    user,
    t,
    mutateAsync,
    setSuperAdmin,
  ]);

  useInterval(
    () => {
      const storeAuthToken = async () => {
        setAuthToken(await getAccessTokenSilently());
      };
      storeAuthToken();
    },
    refresh && isAuthenticated ? TOKEN_REFRESH_RATE : null
  );
}
